import React from "react";
import styled from "styled-components";
import { gradients } from "../theme/gradients";
import { shadows } from "../theme/shadows";

const Button = ({
  children,
  variant = "primary",
  size = "medium",
  loading = false,
  disabled = false,
  onClick,
  className,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      loading={loading}
      disabled={disabled || loading}
      onClick={onClick}
      className={className}
      {...props}
    >
      {loading ? "Loading..." : children}
    </StyledButton>
  );
};

const StyledButton = styled.button`
  border: none;
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  white-space: nowrap;

  /* Size variants */
  ${({ size }) => {
    switch (size) {
      case "small":
        return `
          padding: 8px 20px;
          font-size: 14px;
          min-height: 36px;
        `;
      case "large":
        return `
          padding: 16px 40px;
          font-size: 18px;
          min-height: 56px;
        `;
      default:
        return `
          padding: 12px 32px;
          font-size: 16px;
          min-height: 48px;
        `;
    }
  }}

  /* Style variants */
  ${({ variant }) => {
    switch (variant) {
      case "primary":
        return `
          background: ${gradients.primary};
          color: white;
          box-shadow: ${shadows.md};

          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: ${shadows.primary};
          }
        `;
      case "secondary":
        return `
          background: ${gradients.glass};
          color: #2c5aa0;
          border: 2px solid rgba(44, 90, 160, 0.2);
          backdrop-filter: blur(12px);

          &:hover:not(:disabled) {
            background: ${gradients.glassStrong};
            border-color: rgba(44, 90, 160, 0.3);
            transform: translateY(-1px);
            box-shadow: ${shadows.hover.sm};
          }
        `;
      case "outline":
        return `
          background: transparent;
          color: #2c5aa0;
          border: 2px solid #2c5aa0;

          &:hover:not(:disabled) {
            background: #2c5aa0;
            color: white;
            transform: translateY(-1px);
            box-shadow: ${shadows.primary};
          }
        `;
      case "subtle":
        return `
          background: transparent;
          color: #ff0800;
          border: 2px solid #ff0800;

          &:hover:not(:disabled) {
            border-color: #ff0800;
            color: #ff0800;
            transform: translateY(-1px);
            background: rgba(255, 8, 0, 0.05);
          }
        `;
      case "success":
        return `
          background: ${gradients.success};
          color: #27ae60;
          border: 2px solid rgba(46, 204, 113, 0.3);

          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: ${shadows.success};
          }
        `;
      case "error":
        return `
          background: ${gradients.error};
          color: #e74c3c;
          border: 2px solid rgba(231, 76, 60, 0.3);

          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: ${shadows.error};
          }
        `;
      case "destructive":
        return `
          background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
          color: white;
          box-shadow: ${shadows.md};

          &:hover:not(:disabled) {
            background: linear-gradient(135deg, #c0392b 0%, #e74c3c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(231, 76, 60, 0.4);
          }
        `;
      default:
        return `
          background: ${gradients.primary};
          color: white;
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  &:focus {
    outline: none;
    box-shadow: ${shadows.focus};
  }

  /* Loading state */
  ${({ loading }) =>
    loading &&
    `
    opacity: 0.8;
    cursor: wait;
  `}
`;

export default Button;
